import { MutableRefObject, useEffect } from 'react';
import { Vega } from 'react-vega';
import { VegaProps } from 'react-vega/lib/Vega';
import { useElementSize } from '@mantine/hooks';
import clsx from 'clsx';
import { Spec, View } from 'vega';

import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import styles from './vegaChart.module.scss';

export type Data = VegaProps['data'];

export type VegaChartProps = {
  spec: Spec;
  className?: string;
  data?: Data;
  heightfactor: number;
  isFullscreen?: boolean;
  loading?: boolean;
  minHeight?: number;
  vegaViewRef?: MutableRefObject<View | null>;
  onReadyCallback?: (v: View) => void;
};

const calculateVegaChartHeight = (
  isFullscreen: boolean,
  heightfactor: number
) => (isFullscreen ? '90vh' : `${heightfactor * 48}rem`);

export function VegaChart({
  spec,
  data,
  className,
  heightfactor,
  isFullscreen = false,
  loading = false,
  vegaViewRef,
  minHeight,
  onReadyCallback,
}: VegaChartProps) {
  const {
    ref: wrapperRef,
    height: wrapperHeight,
    width: wrapperWidth,
  } = useElementSize();

  useEffect(() => {
    globalThis.dispatchEvent(new CustomEvent('resizevegachart'));
  }, [wrapperHeight, wrapperWidth]);

  return (
    <div
      className={clsx(styles['root'], className)}
      data-testid="vega-chart_root"
      // scale the height factor in frontend
      // 100% -> should take most of the space, but it should be clear if there is more content
      // 50% -> two elements should take most of the space, but it should be clear if there is more content
      // scaling value may need future adjustments
      style={{
        height: calculateVegaChartHeight(isFullscreen, heightfactor),
        minHeight,
      }}
      ref={wrapperRef}
    >
      {loading && (
        <div className={styles['overlay']}>
          <LoadingIndicator data-testid="vega-chart_loading-indicator" />
        </div>
      )}
      <Vega
        onNewView={
          vegaViewRef != null
            ? (view) => {
                vegaViewRef.current = view;
                onReadyCallback?.(view);
              }
            : undefined
        }
        onError={(err) => {
          console.error('Vega onError', err);
          throw err;
        }}
        spec={spec}
        data={data}
        className={styles['vega']}
        actions={false}
        renderer="svg"
      />
    </div>
  );
}
